import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AddButton,
  Chip,
  ModalNewStaff,
  SearchInput,
  Table,
  TableItem,
} from "../../components";
import { ReactComponent as ChevronIcon } from "../../assets/icons/chevron_right.svg";
import styles from "./StudentsScreen.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { selectStudents } from "../../store/slices/student/slice";
import debounce from "lodash.debounce";
import { fetchStudents } from "../../store/slices/student/asyncThunks";
import { ORDER } from "../../components/Table/Table";
import { ROLES } from "../../utils/constants";
import { selectLoggedInUserRole } from "../../store/slices/user/slice";

const StudentsScreen = () => {
  const dispatch = useDispatch();
  let page = +useLocation().search.split("=")[1];
  let navigate = useNavigate();

  const { data, perPage, total } = useSelector(selectStudents);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortOptions, setSortOptions] = useState({
    sortBy: "",
    order: ORDER.ASC,
  });
  const [selectedStudent, setSelectedStudent] = useState(null);

  const loggedInUserRole = useSelector(selectLoggedInUserRole);

  const newStudentModal = useRef();

  const tableHeader = [
    {
      key: "firstName",
      label: "Name",
      sortable: true,
    },
    {
      key: "externalId",
      label: "Student ID",
      sortable: true,
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
    },
    {
      key: "graduationYear",
      label: "Graduation Year",
      sortable: true,
    },
    {
      key: "",
      label: "",
    },
  ];

  const setPage = async (page) => {
    setCurrentPage(page);
    navigate({
      pathname: "/students",
      search: `?page=${page}`,
    });
  };

  const changeHandler = useCallback(
    (text) => {
      setSearch(text);
      dispatch(
        fetchStudents({
          page: Number(page),
          sortBy: sortOptions.sortBy,
          order: sortOptions.order,
          search: text,
        })
      );
    },
    [page, sortOptions, dispatch]
  );

  const debouncedChangeHandler = useMemo(
    () => debounce(changeHandler, 300),
    [changeHandler]
  );

  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
    };
  });

  useEffect(() => {
    setCurrentPage(page);
    if (page > 0) {
      dispatch(
        fetchStudents({
          page: Number(page),
          sortBy: sortOptions.sortBy,
          order: sortOptions.order,
          search: search,
        })
      );
    }
    //eslint-disable-next-line
  }, [page, sortOptions, dispatch]);

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>Students</h3>
      <div className={styles.header}>
        <SearchInput
          onChange={debouncedChangeHandler}
          placeholder="Search Students"
        />
        {loggedInUserRole === ROLES.ADMIN && (
          <div className={styles.buttonContainer}>
            {/* <Button title="Import CSV" className={styles.importCsvBtn} /> */}
            <AddButton
              title="Add Student"
              onClick={() => {
                setSelectedStudent(null);
                newStudentModal.current.show();
              }}
            />
          </div>
        )}
      </div>
      <div className={styles.content}>
        <Table
          currentPage={currentPage}
          totalCount={total}
          pageSize={perPage}
          setPage={setPage}
          noDataContent="No Data"
          setSortOptions={setSortOptions}
          sortOptions={sortOptions}
          headerInformation={tableHeader}
        >
          {data.map((student, index) => {
            return (
              <Fragment key={index}>
                <TableItem
                  data={
                    <div className={styles.nameContainer}>
                      <Chip
                        img={student.profilePhoto}
                        firstName={student.firstName}
                        lastName={student.lastName}
                      />
                      <p className={styles.nameText}>
                        {student.firstName + " " + student.lastName}
                      </p>
                    </div>
                  }
                />
                <TableItem data={student.externalId} />
                <TableItem data={student.email} />
                <TableItem data={student.graduationYear} />
                <TableItem
                  data={
                    <div
                      className={styles.iconContainer}
                      onClick={() => {
                        setSelectedStudent(student);
                        newStudentModal.current.show();
                      }}
                    >
                      <ChevronIcon className={styles.icon} />
                    </div>
                  }
                />
              </Fragment>
            );
          })}
        </Table>
      </div>
      <ModalNewStaff
        staffType={ROLES.STUDENT}
        modalRef={newStudentModal}
        selectedStudent={selectedStudent}
      />
    </div>
  );
};

export default StudentsScreen;
